import './config';

import { A7ButtonModule } from '@ark7/button';
import { A7InputModule } from '@ark7/input';
import {
  A7LayoutModule,
  A7LayoutService,
  GLOBAL_CUSTOM_DIMENSIONS,
  GtagService,
} from '@ark7/layout';
import {
  A7ResourcesCommonModule,
  A7_RESOURCES_USER,
  API_CLIENT_INFO,
} from '@ark7/resources-common';
import { A7SharedComponentsModule } from '@ark7/shared-components';
import { A7SocketModule, A7_SOCKET_HOST } from '@ark7/socket';
import { A7UiAuthModule } from '@ark7/ui-auth';
import { A7UtilsModule, APPLICATION_META } from '@ark7/utils';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  BrowserModule,
} from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import {
  FaIconLibrary,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgModule } from '@angular/core';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { ReactiveFormsModule } from '@angular/forms';
import { ResourceHandler } from '@ark7/resource2';
import { ResourceHandlerHttpClient } from '@ngx-resource/handler-ngx-http';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { faApple } from '@fortawesome/free-brands-svg-icons/faApple';
import { faFacebookSquare } from '@fortawesome/free-brands-svg-icons/faFacebookSquare';
import { faGoogle } from '@fortawesome/free-brands-svg-icons/faGoogle';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';

import packageInfo from '../../../../package.json';
import { AndesCustomerRelationshipSummaryComponent } from './andes-customer-relationship-summary/andes-customer-relationship-summary.component';
import { AndesSecondaryMarketTradingAgreementComponent } from './andes-secondary-market-trading-agreement/andes-secondary-market-trading-agreement.component';
import { AppComponent } from './app.component';
import { ContactComponent } from './contact/contact.component';
import { DalmoreDisclosureComponent } from './dalmore-disclosure/dalmore-disclosure.component';
import { DalmoreSecondaryMarketTradingAgreementComponent } from './dalmore-secondary-market-trading-agreement/dalmore-secondary-market-trading-agreement.component';
import { DownloadLinkComponent } from './download/download-link/download-link.component';
import { EmailPreferencesComponent } from './email-preferences/email-preferences.component';
import { FAQArticleComponent } from './help/faq-article/faq-article.component';
import { FAQCategoryComponent } from './help/faq-category/faq-category.component';
import { HelpComponent } from './help/help.component';
import { Mobile2Component } from './download/mobile2/mobile2.component';
import { PageModule } from './page/page.module';
import { PromoTermsComponent } from './promo-terms/promo-terms.component';
import { ReferralInvitationComponent } from './join/referral-invitation/referral-invitation.component';
import { ReferralInvitationResultComponent } from './join/referral-invitation-result/referral-invitation-result.component';
import { RoutingModule } from './routing.module';
import { UserResource } from './resources/user-resource';
import { WebV2Module } from './web-v2/web-v2.module';
import { WebV2PartsModule } from './web-v2/parts/v2-parts.module';
import { WebinarCardComponent } from './help/webinar-card/webinar-card.component';
import { environment } from '../environments/environment';

/**
 * We have to import above first.
 */

let isBrowser: boolean = true;
// Can't just embed to isBrowser, angular complaints it.
if (typeof window === 'undefined') {
  isBrowser = false;
}
@NgModule({
  declarations: [
    AppComponent,
    Mobile2Component,
    ContactComponent,
    HelpComponent,
    FAQArticleComponent,
    FAQCategoryComponent,
    WebinarCardComponent,
    DownloadLinkComponent,
    EmailPreferencesComponent,
    ReferralInvitationComponent,
    ReferralInvitationResultComponent,
    PromoTermsComponent,
    DalmoreDisclosureComponent,
    DalmoreSecondaryMarketTradingAgreementComponent,
    AndesSecondaryMarketTradingAgreementComponent,
    AndesCustomerRelationshipSummaryComponent,
  ],
  imports: [
    A7UtilsModule,
    A7LayoutModule.forRoot({
      gtag: {
        trackingId: environment.gtag.trackingId,
        ga4TrackingId: environment.gtag.ga4TrackingId,
        adsTrackingId: environment.gtag.adsTrackingId,
        adsConversionSignUp: environment.gtag.adsConversionSignUp,
        adsConversionInvestment: environment.gtag.adsConversionInvestment,
        dimensions: GLOBAL_CUSTOM_DIMENSIONS,
      },
    }),
    A7ResourcesCommonModule.forRoot({
      Auth: environment.cognito.Auth,
      logErrorsToServer: isBrowser,
    }),
    A7InputModule,
    A7SharedComponentsModule,
    A7SocketModule,
    A7UiAuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    FontAwesomeModule,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatInputModule,
    MatSlideToggleModule,
    NgxWebstorageModule.forRoot(),
    PageModule,
    ReactiveFormsModule,
    RoutingModule,
    ScrollToModule.forRoot(),
    WebV2Module,
    WebV2PartsModule,
    A7ButtonModule,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: A7_RESOURCES_USER,
      useClass: UserResource,
    },
    {
      provide: APPLICATION_META,
      useValue: {
        serviceName: 'brontosaurus',
        env: environment.env,
        version: packageInfo.version,
      },
    },
    {
      provide: API_CLIENT_INFO,
      useValue: {
        clientId: environment.clientId,
        clientSecret: environment.clientSecret,
      },
    },
    {
      provide: A7_SOCKET_HOST,
      useValue: environment.apiHost,
    },
    // ResourceHandler has been passed through from @ark7/resource, Universal
    // doesn't work with ResourceModule.forRoot() since it's not the original
    // one.
    {
      provide: ResourceHandler,
      useClass: ResourceHandlerHttpClient,
      deps: [HttpClient],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    library: FaIconLibrary,
    _layout: A7LayoutService,
    _gtag: GtagService,
  ) {
    library.addIcons(
      faBars,
      faTimes,
      faApple,
      faGoogle,
      faFacebookSquare,
    );
    /**
     * Without below, we can not generate blg.js from webpack
     */
    // helloWorldBlog();
  }
}